import { useEffect, useMemo, useRef, useState } from "react"
import { GetApp } from "../App"
import { Localization, ChangeLogTexts } from "../App/Localization"
import { localStorageValue } from "../App/LocalStorage"
import { Overlay, OverlayTitle } from "../Components/Overlay"
import { Icon, Icons } from "../Components/Icon"
import { Block, BlockContainer, BlockField, BlockRow } from "../Components/Block"
import { LocalizedMessage } from "../Components/LocalizedMessage"
import { ApiClient, Routes } from "../Types"

interface ChangeLogViewProps {
  platform?: string;
  showExternal?: boolean;
  onCloseExternal?: () => void;
  changeLog?: ChangeLogTexts;
}

interface IReleaseInfo {
  version: string;
  date: string;
  platforms: IReleasePlatformInfo[];
}

interface IReleasePlatformInfo {
  name: string;
  version: string;
  date: string;
}

interface IReleaseResponse {
  release: IReleaseInfo;
  previous?: IReleaseInfo;
  next?: IReleaseInfo;
}

export const ChangeLogViewData = {
  loadRelease(api: ApiClient): Promise<IReleaseResponse> {
    return new Routes(api).release.get({ params: {} }).call();
  },

  // Changelog version keys are date strings; newest first.
  sortedVersions(changeLog: { [version: string]: unknown }): string[] {
    return Object.keys(changeLog).toSorted().toReversed();
  },

  // A newer build exists when the reported available version sorts after the current one.
  newVersionAvailable(currentVersion?: string, availableVersion?: string): boolean {
    return !!(availableVersion && currentVersion && currentVersion < availableVersion);
  },

  // The versions to render: everything when shown externally, otherwise only entries
  // newer than the last version the user has already seen.
  visibleVersions(versions: string[], lastVersion?: string, showExternal?: boolean): string[] {
    return versions.filter((v) => !!showExternal || !lastVersion || Date.parse(v) > Date.parse(lastVersion));
  },
};

export function ChangeLogView({ platform, showExternal, onCloseExternal, changeLog: changeLogProp }: ChangeLogViewProps) {
  const app = GetApp()!;
  const companion = app.localSession();
  const [availableVersion, setAvailableVersion] = useState<string | undefined>(undefined);
  const [show, setShow] = useState<boolean>(false);
  const dismissed = useRef(false);
  const [lastVersion, _setLastVersion] = useState<string | undefined>(localStorageValue("logbook-version"));
  const t = app.getT("ChangeLogView");

  // The changelog is normally sourced from the localization bundle, which grows
  // with every release; callers (e.g. the catalog fixture) can inject a fixed,
  // bounded changelog so their rendering stays deterministic and doesn't drift.
  const changeLog = changeLogProp ?? Localization.Messages[app.language].changeLog;
  const versions = useMemo(() => ChangeLogViewData.sortedVersions(changeLog), []);
  const currentVersion = useMemo(() => versions[0], [versions]);
  const newVersionAvailable = useMemo(() => ChangeLogViewData.newVersionAvailable(currentVersion, availableVersion), [availableVersion, currentVersion]);

  useEffect(() => {
    if (app.cloudSession()) {
      return;
    }
    if (!availableVersion) {
      const load = async () => {
        const release = await ChangeLogViewData.loadRelease(app.apiClient);
        if (release && release.release && release.release.version) {
          if (platform && release.release.platforms) {
            const platformInfo = release.release.platforms.find((p) => p.name === platform);
            if (platformInfo) {
              setAvailableVersion(platformInfo.date);
              return;
            }
          }
          setAvailableVersion(release.release.version);
        }
      };
      load().catch(console.error);
    }
  }, [availableVersion, platform]);

  useEffect(() => {
    if (companion || dismissed.current) {
      return;
    }
    if (availableVersion && currentVersion < availableVersion) {
      setShow(true);
      localStorage.setItem("logbook-version", currentVersion);
    } else if (!lastVersion || lastVersion !== currentVersion) {
      setShow(true);
      localStorage.setItem("logbook-version", currentVersion);
    }
  }, [companion, lastVersion, currentVersion, availableVersion]);

  useEffect(() => {
    if (showExternal) {
      setShow(true);
    }
  }, [showExternal]);

  const changeLogs = useMemo(() => {
    return ChangeLogViewData.visibleVersions(versions, lastVersion, showExternal).map((v) => {
      const message = changeLog[v];
      return (
        <>
          <BlockRow key={`changelog-${v}`}>
            <h4>{new Date(Date.parse(v)).toLocaleDateString()}</h4>
          </BlockRow>
          <BlockRow>
            <LocalizedMessage message={() => message} html={true} />
          </BlockRow>
        </>
      );
    });
  }, [versions, showExternal]);

  if (!show) {
    return null;
  }

  const content = (
    <>
      <OverlayTitle title={t("change_log")} closeCallback={() => {
        dismissed.current = true;
        setShow(false);
        if (onCloseExternal) {
          onCloseExternal();
        }
      }}
      />
      <BlockContainer className="Overlay-body ChangeLogView ">
        <Block className="ChangeLogView-content">
          {newVersionAvailable && (
            <BlockRow className="ChangeLogView-update-notice">
              <BlockField>
                <a href={`https://logbook.yachts/site/${app.language}/download`} target="_blank" rel="noreferrer">
                  <Icon className="ChangeLogView-update-icon" icon={Icons.Refresh} />
                  {t("new_version_available", { version: availableVersion })}
                </a>
              </BlockField>
            </BlockRow>
          )}
          <BlockRow><h2>{t("title")}</h2></BlockRow>
          <BlockRow><h5>{t("change_welcome", { version: app.t("LogbookApp_version_major") })}</h5></BlockRow>
          <BlockRow><p>{t("change_fixed_info")}</p></BlockRow>
          {changeLogs}
        </Block>
      </BlockContainer>
    </>
  );

  if (!showExternal) {
    return <Overlay>{content}</Overlay>;
  }

  return content;
}
